/**
 * Competition field validation (save + publish). ADR-028 management.
 */

import { COMPETITION_STATUSES } from "./competitionTypes";
import { normalizeCompetitionStatus } from "./competitionStatusTransitions";

/**
 * @typedef {Object} CompetitionValidationResult
 * @property {boolean} ok
 * @property {Record<string, string>} fieldErrors
 * @property {string[]} errors
 */

/**
 * @returns {CompetitionValidationResult}
 */
function emptyResult() {
  return { ok: true, fieldErrors: {}, errors: [] };
}

/**
 * @param {CompetitionValidationResult} result
 * @param {string} field
 * @param {string} message
 */
function addError(result, field, message) {
  result.ok = false;
  if (field && !result.fieldErrors[field]) {
    result.fieldErrors[field] = message;
  }
  if (!result.errors.includes(message)) {
    result.errors.push(message);
  }
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Closing registration after the listed start must not block later saves.
 * The start moves to the close, and the end moves with it when it would fall earlier.
 * @param {object | null | undefined} dates
 */
export function alignStartOnOrAfterRegistrationClose(dates) {
  const next = { ...(dates || {}) };
  const closeMs = next.registrationCloseMs;
  const startMs = next.startMs;
  if (closeMs != null && startMs != null && Number(closeMs) > Number(startMs)) {
    next.startMs = Number(closeMs);
  }
  if (next.endMs != null && next.startMs != null && Number(next.endMs) < Number(next.startMs)) {
    next.endMs = Number(next.startMs) + ONE_DAY_MS;
  }
  return next;
}

/**
 * Validate competition for save (draft or updates).
 * @param {object} competition
 * @param {{ forPublish?: boolean, forDraftSave?: boolean }} [options]
 * @returns {CompetitionValidationResult}
 */
export function validateCompetition(competition, options = {}) {
  const result = emptyResult();
  const forPublish = Boolean(options.forPublish);
  const forDraftSave = Boolean(options.forDraftSave) && !forPublish;
  const c = competition || {};

  const name = (c.name || "").trim();
  if (!name) {
    addError(result, "name", "Competition name is required.");
  }

  const organizerName = (c.organizerName || c.organizer?.name || "").trim();
  const organizerUid = c.organizerUid || c.organizer?.uid || c.createdBy || "";
  if (!organizerName && !organizerUid) {
    addError(result, "organizerName", "Organizer is required.");
  }

  if (forDraftSave) {
    return result;
  }

  const startMs = c.dates?.startMs ?? c.startMs ?? null;
  const endMs = c.dates?.endMs ?? c.endMs ?? null;
  const regOpen = c.dates?.registrationOpenMs ?? null;
  const regClose = c.dates?.registrationCloseMs ?? null;

  if (startMs == null || Number.isNaN(Number(startMs))) {
    addError(result, "dates.startMs", "Competition start date is required.");
  }
  if (endMs == null || Number.isNaN(Number(endMs))) {
    addError(result, "dates.endMs", "Competition end date is required.");
  }
  if (startMs != null && endMs != null && Number(endMs) < Number(startMs)) {
    addError(result, "dates.endMs", "End date must be on or after the start date.");
  }
  if (regOpen != null && regClose != null && Number(regOpen) > Number(regClose)) {
    addError(
      result,
      "dates.registrationCloseMs",
      "Registration close must be on or after registration open."
    );
  }
  if (regClose != null && startMs != null && Number(regClose) > Number(startMs)) {
    addError(
      result,
      "dates.registrationCloseMs",
      "Registration must close on or before competition start."
    );
  }

  const courtCountRaw = c.settings?.courtCount ?? c.courtCount;
  if (courtCountRaw === "") {
    addError(result, "settings.courtCount", "Court count must be at least 1.");
  } else {
    const courtCount = courtCountRaw ?? 1;
    if (courtCount == null || Number(courtCount) < 1) {
      addError(result, "settings.courtCount", "Court count must be at least 1.");
    }
  }

  if (forPublish) {
    if (normalizeCompetitionStatus(c.status) !== COMPETITION_STATUSES.DRAFT) {
      addError(result, "status", "Only draft competitions can be published.");
    }
    const events = c.managedEvents || c.events || [];
    const planned = Array.isArray(c.plannedEventNames) ? c.plannedEventNames : [];
    const eventCount = Array.isArray(events) ? events.length : 0;
    const plannedCount = planned.map((n) => String(n || "").trim()).filter(Boolean).length;
    if (eventCount < 1 && plannedCount < 1) {
      addError(
        result,
        "managedEvents",
        "Publishing requires at least one event. Add an event before publishing."
      );
    }
  }

  return result;
}

/**
 * Step-level validation for the multi-step wizard.
 * @param {number} step 0–4
 * @param {object} form
 * @returns {CompetitionValidationResult}
 */
export function validateCompetitionWizardStep(step, form) {
  const result = emptyResult();
  const f = form || {};

  if (step === 0) {
    if (!(f.name || "").trim()) addError(result, "name", "Competition name is required.");
    if (!(f.organizerName || "").trim()) {
      addError(result, "organizerName", "Organizer is required.");
    }
  }
  // Venue / dates are optional during draft setup (editable on Tournament Home).
  // Only cross-field consistency when values are present.
  if (step === 1) {
    /* venue optional until publish / later setup */
  }
  if (step === 2) {
    if (
      f.dates?.startMs != null &&
      f.dates?.endMs != null &&
      Number(f.dates.endMs) < Number(f.dates.startMs)
    ) {
      addError(result, "dates.endMs", "End date must be on or after the start date.");
    }
    if (
      f.dates?.registrationOpenMs != null &&
      f.dates?.registrationCloseMs != null &&
      Number(f.dates.registrationOpenMs) > Number(f.dates.registrationCloseMs)
    ) {
      addError(
        result,
        "dates.registrationCloseMs",
        "Registration close must be on or after registration open."
      );
    }
    if (
      f.dates?.registrationCloseMs != null &&
      f.dates?.startMs != null &&
      Number(f.dates.registrationCloseMs) > Number(f.dates.startMs)
    ) {
      addError(
        result,
        "dates.registrationCloseMs",
        "Registration must close on or before competition start."
      );
    }
  }
  if (step === 3) {
    const courts = f.settings?.courtCount;
    if (courts === "" || courts == null || Number(courts) < 1) {
      addError(result, "settings.courtCount", "Court count must be at least 1.");
    }
    const points = f.settings?.defaultMatchFormat?.pointsToWin;
    if (points === "" || points == null || Number(points) < 1) {
      addError(
        result,
        "settings.defaultMatchFormat.pointsToWin",
        "Points to win must be at least 1."
      );
    }
    const qualify = f.settings?.standings?.qualifyTopN;
    if (qualify !== "" && qualify != null && Number(qualify) < 0) {
      addError(
        result,
        "settings.standings.qualifyTopN",
        "Qualify Top N cannot be negative."
      );
    }
  }
  // step 4 Events — optional (Main event created on publish if empty)
  if (step === 5) {
    return validateCompetition(formToCompetitionShape(f), { forPublish: false });
  }

  return result;
}

/**
 * Map wizard form → competition-like object for validateCompetition.
 * @param {object} form
 */
export function formToCompetitionShape(form) {
  return {
    name: form.name,
    organizerName: form.organizerName,
    organizerUid: form.organizerUid,
    createdBy: form.createdBy,
    status: form.status || COMPETITION_STATUSES.DRAFT,
    kind: form.kind,
    description: form.description,
    contactEmail: (form.contactEmail || "").trim() || null,
    bannerImageUrl: form.bannerImageUrl,
    logoUrl: form.logoUrl,
    organizationId: form.organizationId || null,
    venue: form.venue,
    dates: form.dates,
    settings: {
      ...(form.settings || {}),
      contactEmail: (form.contactEmail || "").trim() || null,
    },
    managedEvents: form.managedEvents || [],
    setsToWin: form.settings?.defaultMatchFormat?.setsToWin ?? 2,
  };
}
